/**
 * Platform Administration cache keys.
 *
 * UX-contract §7 requires the Platform query cache to be a namespace fully
 * separate from the Competition-scoped cache (`features/competitions/query-keys.ts`)
 * — a stale Platform Users page must never accidentally reuse a
 * competition-scoped cache entry, and vice versa. Every key below starts
 * with `'platform'`, never `'competitions'`, and every list key folds in the
 * full filter/sort/page state so two different filters never collide on one
 * cache entry (UX-010, §7 "filter/sort/page").
 */
export const platformKeys = {
  all: ['platform'] as const,
  dashboard: () => [...platformKeys.all, 'dashboard'] as const,
  users: {
    all: () => [...platformKeys.all, 'users'] as const,
    list: (filters: Record<string, string>) =>
      [...platformKeys.users.all(), 'list', filters] as const,
    detail: (userId: string) => [...platformKeys.users.all(), userId] as const,
    competitions: (userId: string) =>
      [...platformKeys.users.detail(userId), 'competitions'] as const,
  },
  people: {
    all: (competitionId: string) => [...platformKeys.all, 'people', competitionId] as const,
    list: (competitionId: string, filters: Record<string, string>) =>
      [...platformKeys.people.all(competitionId), filters] as const,
  },
  judges: {
    all: () => [...platformKeys.all, 'judges'] as const,
    list: (filters: Record<string, string>) =>
      [...platformKeys.judges.all(), 'list', filters] as const,
    forUser: (userId: string) => [...platformKeys.judges.all(), 'user', userId] as const,
    forCompetition: (competitionId: string, filters: Record<string, string>) =>
      [...platformKeys.judges.all(), 'competition', competitionId, filters] as const,
  },
  audit: {
    all: () => [...platformKeys.all, 'audit'] as const,
    list: (filters: Record<string, string>) => [...platformKeys.audit.all(), filters] as const,
  },
  admins: {
    all: () => [...platformKeys.all, 'admins'] as const,
    list: (filters: Record<string, string>) =>
      [...platformKeys.admins.all(), 'list', filters] as const,
  },
  competitionRequests: {
    all: () => [...platformKeys.all, 'competition-requests'] as const,
    list: (filters: Record<string, string>) =>
      [...platformKeys.competitionRequests.all(), 'list', filters] as const,
    detail: (requestId: string) => [...platformKeys.competitionRequests.all(), requestId] as const,
  },
  erasureRequests: {
    all: () => [...platformKeys.all, 'erasure-requests'] as const,
    list: (filters: Record<string, string>) =>
      [...platformKeys.erasureRequests.all(), 'list', filters] as const,
  },
} as const
